/**
 * CytoscapeGraphCanvas - Neo4j-style Property Graph for Graph-Spec
 *
 * Cytoscape.js-based graph visualization with COSE force-directed layout.
 * Replaces the basic SVG renderer for interactive property graph experience.
 *
 * @module client/components/CytoscapeGraphCanvas
 * @version 1.0
 * @date 2025-10-11
 */

import * as React from "react";
import cytoscape, { Core, EventObject } from "cytoscape";
import {
  GraphSpec,
  GraphNode,
  GraphEdge,
  GraphViewerState,
  FilterState,
} from "../types/graphSpec";

// Extended node/edge types with dimmed property for filtering
interface DimmedNode extends GraphNode {
  dimmed?: boolean;
}
interface DimmedEdge extends GraphEdge {
  dimmed?: boolean;
}

interface CytoscapeGraphCanvasProps {
  graphSpec: GraphSpec;
  viewerState: GraphViewerState;
  onSelectNode: (nodeId: string | null) => void;
  onSelectEdge: (edgeId: string | null) => void;
}

/**
 * Cytoscape Graph Canvas Component
 *
 * Neo4j-style interactive property graph with:
 * - COSE force-directed layout
 * - Pan and zoom
 * - Node/edge selection
 * - Status-based coloring (pass/fail/expected)
 * - View mode filtering (instance/schema)
 */
export function CytoscapeGraphCanvas({
  graphSpec,
  viewerState,
  onSelectNode,
  onSelectEdge,
}: CytoscapeGraphCanvasProps): React.ReactElement {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const cyRef = React.useRef<Core | null>(null);

  React.useEffect(() => {
    if (!containerRef.current) return;

    // Filter graph based on viewer state
    const { nodes, edges } = filterGraph(
      graphSpec,
      viewerState.filter,
      viewerState.search,
      viewerState.viewMode
    );

    if (nodes.length === 0) {
      return;
    }

    // Convert to Cytoscape format
    const elements = [
      ...nodes.map((n) => ({
        data: {
          id: n.id,
          label: n.label,
          type: n.type,
          pillar: n.pillar,
          status: n.status,
          dimmed: (n as DimmedNode).dimmed,
        },
      })),
      ...edges.map((e) => ({
        data: {
          id: e.id,
          source: e.source,
          target: e.target,
          label: e.label,
          predicate: e.predicate,
          status: e.status,
          pillar: e.pillar,
          inferred: e.inferred,
          dimmed: (e as DimmedEdge).dimmed,
        },
      })),
    ];

    // Initialize Cytoscape
    const cy = cytoscape({
      container: containerRef.current,
      elements,
      style: getCytoscapeStyles(),
      layout: {
        name: viewerState.layout === "cose" ? "cose" : "breadthfirst",
        // COSE force-directed layout options
        animate: true,
        animationDuration: 500,
        refresh: 20,
        fit: true,
        padding: 30,
        // COSE-specific
        randomize: false,
        componentSpacing: 100,
        nodeOverlap: 20,
        idealEdgeLength: 100,
        edgeElasticity: 100,
        nestingFactor: 5,
        gravity: 80,
        numIter: 1000,
        initialTemp: 200,
        coolingFactor: 0.95,
        minTemp: 1.0,
      },
      minZoom: 0.2,
      maxZoom: 4,
      wheelSensitivity: 0.15,
    });

    cyRef.current = cy;

    // Node click handler
    cy.on("tap", "node", (event: EventObject) => {
      const node = event.target;
      onSelectNode(node.id());
    });

    // Edge click handler
    cy.on("tap", "edge", (event: EventObject) => {
      const edge = event.target;
      onSelectEdge(edge.id());
    });

    // Background click (deselect)
    cy.on("tap", (event: EventObject) => {
      if (event.target === cy) {
        onSelectNode(null);
        onSelectEdge(null);
      }
    });

    // Cleanup
    return () => {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, [
    graphSpec,
    viewerState.filter,
    viewerState.search,
    viewerState.viewMode,
    viewerState.layout,
    onSelectNode,
    onSelectEdge,
  ]);

  // Handle selection highlighting
  React.useEffect(() => {
    if (!cyRef.current) return;

    const cy = cyRef.current;

    // Clear previous selection
    cy.$("*").removeClass("selected");

    // Apply new selection
    if (viewerState.selection.type === "node" && viewerState.selection.id) {
      cy.getElementById(viewerState.selection.id).addClass("selected");
    } else if (viewerState.selection.type === "edge" && viewerState.selection.id) {
      cy.getElementById(viewerState.selection.id).addClass("selected");
    }
  }, [viewerState.selection]);

  return (
    <div
      ref={containerRef}
      className="cytoscape-graph-canvas"
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: "#ffffff",
      }}
    />
  );
}

/**
 * Filter graph based on filter state and search query
 */
function filterGraph(
  graphSpec: GraphSpec,
  filter: FilterState,
  search: string,
  viewMode: "instance" | "schema"
): { nodes: GraphNode[]; edges: GraphEdge[] } {
  const keepPropertyNodes = filter.showPropertyNodes ?? false;

  // Hard filter nodes by view mode
  const prefilteredNodes = graphSpec.nodes.filter((n) => {
    if (viewMode === "instance") {
      if (n.type === "Class") return false;
      if (n.type === "Rule") return false;
      if (!keepPropertyNodes && n.type === "Property") return false;
      return true;
    } else {
      // Schema view
      if (n.type === "Individual") return false;
      if (!filter.showRuleNodes && n.type === "Rule") return false;
      if (n.type === "Property" && (n as GraphNode & { role?: string }).role === "meta") return false;
      return true;
    }
  });

  // Visual filter (dimmed)
  const nodes = prefilteredNodes.map((n) => {
    let dimmed = false;

    if (filter.status !== "all" && n.status !== filter.status) {
      dimmed = true;
    }

    if (filter.pillar && n.pillar !== filter.pillar) {
      dimmed = true;
    }

    if (!filter.showRuleNodes && n.type === "Rule") {
      dimmed = true;
    }

    if (search.trim()) {
      const searchLower = search.toLowerCase();
      const matches =
        n.label?.toLowerCase().includes(searchLower) ||
        n.id.toLowerCase().includes(searchLower) ||
        n.iri?.toLowerCase().includes(searchLower);

      if (!matches) {
        dimmed = true;
      }
    }

    return { ...n, dimmed };
  });

  const includedNodeIds = new Set(nodes.map((n) => n.id));
  const dimmedNodeIds = new Set(nodes.filter((n) => (n as DimmedNode).dimmed).map((n) => n.id));

  // Filter edges
  const edges = graphSpec.edges
    .filter((e) => includedNodeIds.has(e.source) && includedNodeIds.has(e.target))
    .filter((e) => {
      if (viewMode === "instance") {
        return e.kind === "abox";
      } else {
        return e.kind === "tbox";
      }
    })
    .map((e) => {
      let dimmed = false;

      if (filter.status !== "all" && e.status !== filter.status) {
        dimmed = true;
      }

      if (filter.pillar && e.pillar !== filter.pillar) {
        dimmed = true;
      }

      if (!filter.showInferred && e.inferred) {
        dimmed = true;
      }

      if (dimmedNodeIds.has(e.source) || dimmedNodeIds.has(e.target)) {
        dimmed = true;
      }

      return { ...e, dimmed };
    });

  return { nodes, edges };
}

/**
 * Get Cytoscape.js stylesheet
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- cytoscape Stylesheet type was removed in newer versions
function getCytoscapeStyles(): any[] {
  return [
    // Base node style
    {
      selector: "node",
      style: {
        label: "data(label)",
        "text-valign": "center",
        "text-halign": "center",
        "font-size": "11px",
        "font-weight": "500",
        color: "#1f2937",
        "background-color": "#94a3b8",
        width: 50,
        height: 50,
        "border-width": 2,
        "border-color": "#64748b",
        "text-wrap": "wrap",
        "text-max-width": "80px",
      },
    },

    // Node types
    {
      selector: 'node[type="Class"]',
      style: {
        "background-color": "#3b82f6", // blue
        shape: "round-rectangle",
      },
    },
    {
      selector: 'node[type="Property"]',
      style: {
        "background-color": "#f59e0b", // amber
        shape: "diamond",
        width: 40,
        height: 40,
      },
    },
    {
      selector: 'node[type="Individual"]',
      style: {
        "background-color": "#8b5cf6", // purple
        shape: "ellipse",
      },
    },
    {
      selector: 'node[type="Rule"]',
      style: {
        "background-color": "#6366f1", // indigo
        shape: "hexagon",
      },
    },

    // Node status rings
    {
      selector: 'node[status="pass"]',
      style: {
        "border-color": "#10b981",
        "border-width": 3,
      },
    },
    {
      selector: 'node[status="fail"]',
      style: {
        "border-color": "#ef4444",
        "border-width": 3,
      },
    },
    {
      selector: 'node[status="expected"]',
      style: {
        "background-color": "#d1d5db",
        "border-color": "#9ca3af",
      },
    },

    // Dimmed nodes
    {
      selector: 'node[dimmed="true"]',
      style: {
        opacity: 0.2,
      },
    },

    // Base edge style
    {
      selector: "edge",
      style: {
        label: "data(label)",
        "font-size": "9px",
        "curve-style": "bezier",
        "target-arrow-shape": "triangle",
        "line-color": "#94a3b8",
        "target-arrow-color": "#94a3b8",
        width: 2,
        "text-background-color": "#ffffff",
        "text-background-opacity": 0.8,
        "text-background-padding": "3px",
      },
    },

    // Edge status
    {
      selector: 'edge[status="pass"]',
      style: {
        "line-color": "#10b981",
        "target-arrow-color": "#10b981",
        width: 3,
      },
    },
    {
      selector: 'edge[status="fail"]',
      style: {
        "line-color": "#ef4444",
        "target-arrow-color": "#ef4444",
        "line-style": "dashed",
      },
    },
    {
      selector: 'edge[status="expected"]',
      style: {
        "line-color": "#9ca3af",
        "target-arrow-color": "#9ca3af",
        "line-style": "dashed",
        width: 2,
      },
    },
    {
      selector: 'edge[status="unknown"]',
      style: {
        "line-color": "#f59e0b",
        "target-arrow-color": "#f59e0b",
      },
    },

    // Inferred edges
    {
      selector: 'edge[inferred="true"]',
      style: {
        "line-style": "dotted",
        width: 1,
      },
    },

    // Dimmed edges
    {
      selector: 'edge[dimmed="true"]',
      style: {
        opacity: 0.15,
      },
    },

    // Selection
    {
      selector: ".selected",
      style: {
        "border-width": 4,
        "border-color": "#fbbf24",
        "background-color": "#fde68a",
        "line-color": "#fbbf24",
        "target-arrow-color": "#fbbf24",
        width: 4,
      },
    },
  ];
}
